import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Users, ArrowRight, Check, Copy } from "lucide-react";import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Collective gift form modal.
 * Step 1: gather group info (name, creator, phone, participants).
 * Step 2: redirect to the shop in "collective mode" so the creator picks one product.
 * The reservation itself (with SMS) is created from the product page once a product is selected.
 */
export default function CollectiveGiftModal({ open, onOpenChange }) {
  const { t } = useLanguage();
  const { code } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    group_name: "",
    creator_name: "",
    creator_phone: "",
    participants_count: 2,
  });
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (
      !form.group_name.trim() ||
      !form.creator_name.trim() ||
      !form.creator_phone.trim()
    ) {
      setError(t("collective.formError"));
      return;
    }
    const participants = parseInt(form.participants_count, 10);
    if (!participants || participants < 2) {
      setError(t("collective.participantsMin"));
      return;
    }
    // Store group data in sessionStorage so the next step (shop -> product detail)
    // can create the reservation with this payload.
    sessionStorage.setItem(
      "milkado_collective",
      JSON.stringify({ ...form, participants_count: participants, event_code: code }),
    );
    onOpenChange(false);
    navigate(`/event/${code}/shop?collective=1`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="collective-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-[#E8F6E5] text-[#3FA535] flex items-center justify-center mb-2">
            <Users className="w-5 h-5" />
          </div>
          <DialogTitle className="font-display text-2xl font-black text-[#2A0E3C]">
            {t("collective.title")}
          </DialogTitle>
          <DialogDescription className="text-[#5C4664]">
            {t("collective.sub")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <Row label={t("collective.groupName")}>
            <Input
              data-testid="collective-group-name"
              value={form.group_name}
              onChange={(e) => setField("group_name", e.target.value)}
              placeholder={t("collective.groupNamePh")}
              className="h-11 rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
            />
          </Row>
          <Row label={t("collective.creatorName")}>
            <Input
              data-testid="collective-creator-name"
              value={form.creator_name}
              onChange={(e) => setField("creator_name", e.target.value)}
              className="h-11 rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
            />
          </Row>
          <Row label={t("collective.creatorPhone")}>
            <Input
              data-testid="collective-creator-phone"
              value={form.creator_phone}
              onChange={(e) => setField("creator_phone", e.target.value)}
              placeholder={t("collective.creatorPhonePh")}
              className="h-11 rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
            />
          </Row>
          <Row label={t("collective.participantsCount")}>
            <Input
              data-testid="collective-participants"
              type="number"
              min="2"
              value={form.participants_count}
              onChange={(e) => setField("participants_count", e.target.value)}
              className="h-11 rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
            />
          </Row>

          {error && (
            <p data-testid="collective-error" className="text-sm text-[#D81B8C] font-medium">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="collective-next"
            className="w-full rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-white h-12 font-semibold gap-2"
          >
            {t("collective.next")}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * Reservation success modal — shown once a reservation has been created.
 * Receives the reservation object (with code, expires_at, sms_sent_to, product_name).
 */
export function ReservationSuccessModal({ reservation, open, onOpenChange }) {
  const { t, lang } = useLanguage();
  const [copied, setCopied] = useState(false);

  if (!reservation) return null;

  const expiresDate = new Date(reservation.expires_at);
  const expiresLabel = expiresDate.toLocaleDateString(
    lang === "fr" ? "fr-FR" : "en-US",
    { day: "numeric", month: "long", year: "numeric" },
  );

  const copy = () => {
    navigator.clipboard.writeText(reservation.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const whatsappMessage = t("collective.shareMessage")
    .replace("{group}", reservation.group_name || "")
    .replace("{product}", reservation.product_name || "")
    .replace("{code}", reservation.code)
    .replace("{expires}", expiresLabel);
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="reservation-success">
        <DialogHeader>
          <div className="w-14 h-14 rounded-full bg-[#3FA535] text-white flex items-center justify-center mb-2 mx-auto">
            <Check className="w-6 h-6" />
          </div>
          <DialogTitle className="font-display text-2xl font-black text-[#2A0E3C] text-center">
            {t("collective.successTitle")}
          </DialogTitle>
          <DialogDescription className="text-[#5C4664] text-center">
            {t("collective.successSub")}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl p-5 text-center"
             style={{ background: "linear-gradient(135deg, #FCE7F3 0%, #EFE3F5 50%, #E8F6E5 100%)" }}>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C4664]">
            {t("collective.yourCode")}
          </p>
          <div className="mt-2 flex items-center justify-center gap-2">
            <span
              data-testid="reservation-code"
              className="font-display text-5xl font-black tracking-[0.35em] text-[#D81B8C]"
            >
              {reservation.code}
            </span>
            <button
              onClick={copy}
              data-testid="reservation-code-copy"
              className="w-9 h-9 rounded-full bg-white hover:bg-[#FCE7F3] flex items-center justify-center text-[#2A0E3C]"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="mt-3 text-xs text-[#5C4664]">
            {t("collective.expiresOn")} <strong>{expiresLabel}</strong>
          </p>
        </div>

        {reservation.product_name && (
          <p className="text-sm text-[#2A0E3C] text-center">
            <span className="font-bold">{reservation.product_name}</span>
            {reservation.product_price ? ` — $${reservation.product_price}` : null}
          </p>
        )}

        <p className="text-xs text-[#5C4664] leading-relaxed">
          {t("collective.smsNote")}{" "}
          <span className="italic">{t("collective.smsMockedNote")}</span>
        </p>

        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="reservation-share-whatsapp"
          className="milk-shimmer milk-glow-green mt-1 w-full flex items-center justify-center gap-2 rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-white h-12 font-semibold transition-colors"
        >
          <svg viewBox="0 0 32 32" className="w-5 h-5 fill-current" aria-hidden="true">
            <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.317.205-.66.205-1.005 0-.058 0-.115-.014-.158-.044-.144-2.378-1.276-2.638-1.276zm-2.967 7.83c-1.563 0-3.083-.43-4.405-1.234l-3.165 1.005 1.03-3.05a8.385 8.385 0 0 1-1.633-4.97c0-4.654 3.794-8.435 8.452-8.435a8.31 8.31 0 0 1 5.97 2.484 8.32 8.32 0 0 1 2.473 5.967c-.014 4.655-3.808 8.235-8.722 8.235zm0-18.853c-5.749 0-10.42 4.67-10.42 10.405 0 1.834.47 3.55 1.347 5.04l-1.5 4.36 4.534-1.434c1.45.78 3.04 1.117 4.67 1.117 5.748 0 10.42-4.67 10.42-10.405-.115-5.738-4.788-10.408-10.421-10.408z"/>
          </svg>
          {t("collective.shareWhatsapp")}
        </a>
      </DialogContent>
    </Dialog>
  );
}
